import { InfoCircleOutlined } from "@ant-design/icons";
import { App, Flex, Form, Popconfirm } from "antd";
import { type FC, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { patchMarketV2Archive } from "tm-shared/api";

import { useAppDispatch } from "@/hooks/redux";
import BaseButton from "@/shared/ui/BaseButton";
import { BaseModal } from "@/shared/ui/BaseModal";
import { PrimaryText } from "@/shared/ui/Typography";
import {
    setAddressVerifyDuplicateResolution,
    setPointCreatePageDuplicateResolution,
} from "@/store/slices/ui";
import type { DuplicateByAddressModalPayload } from "@/types/modals";

import type { PointRelationsFormValues } from "./DuplicateByAddressModal.types";
import { buildPointChains } from "./DuplicateByAddressModal.utilities";
import { PointRelationRow } from "./PointRelationRow";
import { StyledTooltip } from "./Styled";
import { TooltipTextComponent } from "./TooltipTextComponent";

interface DuplicateByAddressModalProps {
    onClose: () => void;
    payload: DuplicateByAddressModalPayload;
}

export const DuplicateByAddressModal: FC<DuplicateByAddressModalProps> = ({
    onClose,
    payload,
}) => {
    const { notification } = App.useApp();

    const { address, points } = payload;

    const dispatch = useAppDispatch();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const methods = useForm<PointRelationsFormValues>({
        defaultValues: {},
    });

    const {
        handleSubmit,
        trigger,
        formState: { isSubmitting },
    } = methods;

    const formValues = useWatch({ control: methods.control });

    const parentId = Object.entries(formValues).find(
        ([_marketField, value]) => value === "parent",
    );

    const hasParentSelected = Boolean(parentId);

    const onSubmit = async (data: PointRelationsFormValues) => {
        const archivedMarkets = Object.entries(data).reduce<string[]>(
            (accumulator, [marketId, value]) => {
                if (value === "archived") {
                    accumulator.push(marketId);
                }

                return accumulator;
            },
            [],
        );

        if (archivedMarkets.length > 0) {
            const results = await Promise.allSettled(
                archivedMarkets.map((marketId) =>
                    patchMarketV2Archive({ marketId }),
                ),
            );

            const failedMarketIds = results.flatMap((result, index) =>
                result.status === "rejected" ? [archivedMarkets[index]] : [],
            );

            if (failedMarketIds.length > 0) {
                console.error("Не вдалося заархівувати ТТ:", failedMarketIds);

                notification.error({
                    title: `Не вдалося заархівувати ${failedMarketIds.length} ТТ`,
                });
            }
        }

        const resolution = {
            confirmed: true,
            ...(parentId && { parentId: parentId[0] }),
        };

        if (payload.variant === "verifyAddress") {
            dispatch(setAddressVerifyDuplicateResolution(resolution));
        } else {
            dispatch(setPointCreatePageDuplicateResolution(resolution));
        }

        onClose();
    };

    const handleBeforeOpen = async () => {
        const isValid = await trigger();

        if (!isValid) {
            setConfirmOpen(false);
            return;
        }

        setConfirmOpen(true);
    };

    const pointsTree = buildPointChains(points);

    return (
        <BaseModal
            open
            onClose={onClose}
            mask={{ closable: false }}
            actions={null}
            title={"Увага"}
            closable={false}
        >
            <FormProvider {...methods}>
                <Form layout="vertical">
                    <Flex vertical gap={16}>
                        <PrimaryText>
                            За адресою {address} знайдено торгові точки
                        </PrimaryText>

                        <Flex
                            vertical
                            gap={12}
                            style={{ overflow: "auto", minHeight: 0 }}
                        >
                            {pointsTree.map((point) => (
                                <PointRelationRow
                                    key={point.id}
                                    point={point}
                                    level={0}
                                    formValues={formValues}
                                    hasParentSelected={hasParentSelected}
                                    setValue={(name, value) =>
                                        methods.setValue(name, value, {
                                            shouldDirty: true,
                                        })
                                    }
                                />
                            ))}
                        </Flex>

                        <Flex vertical gap={12}>
                            <StyledTooltip title={<TooltipTextComponent />}>
                                <Flex gap={8} align="center">
                                    <PrimaryText
                                        fontSize={18}
                                        textColor="warning"
                                    >
                                        <InfoCircleOutlined />
                                    </PrimaryText>

                                    <PrimaryText fontWeight="500">
                                        Інформація про відношення
                                    </PrimaryText>
                                </Flex>
                            </StyledTooltip>

                            <BaseButton
                                type="default"
                                size="large"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Повернутися назад
                            </BaseButton>

                            <Popconfirm
                                open={confirmOpen}
                                onOpenChange={(open) => {
                                    if (!open) {
                                        setConfirmOpen(false);
                                    }
                                }}
                                title={
                                    <Flex vertical gap={8}>
                                        <PrimaryText>
                                            Після підтвердження зміни:
                                        </PrimaryText>

                                        <TooltipTextComponent />

                                        <PrimaryText
                                            fontWeight={"700"}
                                            textColor="error"
                                        >
                                            Цю дію неможливо скасувати!
                                        </PrimaryText>
                                    </Flex>
                                }
                                onConfirm={() => {
                                    setConfirmOpen(false);
                                    void handleSubmit(onSubmit)();
                                }}
                                onCancel={() => {
                                    setConfirmOpen(false);
                                }}
                                okText={"Так"}
                                cancelText={"Ні"}
                            >
                                <BaseButton
                                    type="primary"
                                    size="large"
                                    htmlType="button"
                                    loading={isSubmitting}
                                    onClick={() => {
                                        void handleBeforeOpen();
                                    }}
                                >
                                    Зберегти
                                </BaseButton>
                            </Popconfirm>
                        </Flex>
                    </Flex>
                </Form>
            </FormProvider>
        </BaseModal>
    );
};
